import * as Sentry from '@sentry/react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { z, ZodSchema } from 'zod';
import { zodI18nMap } from 'zod-i18n-map';

import { Language } from './const';
import { getLanguageResources } from './locales';
import { getQueryLanguageOverride } from './queryLanguageOverride';

const DEFAULT_LANGUAGE = Language.ENGLISH;

const init = () => {
	i18n
		.use(initReactI18next)
		.init({
			debug: false,
			resources: {
				'en-US': {
					translation: {},
					zod: {},
				},
			},
			lng: DEFAULT_LANGUAGE,
			fallbackLng: DEFAULT_LANGUAGE,
			interpolation: {
				escapeValue: false,
			},
			partialBundledLanguages: true,
			nsSeparator: false,
			keySeparator: false,
		})
		.then(() => {
			applyQueryLanguageOverride();
		})
		.catch(console.error);
};

const i18p = (str: string) => str;
const t = (...args: any) => (i18n.isInitialized ? i18n.t(...args) : args);
z.setErrorMap(zodI18nMap);
const fetchingLocale: Record<string, boolean> = {};

i18n.on('languageChanged', async (locale: string) => {
	try {
		Sentry.setTag('page_locale', locale);
		if (
			!i18n.hasResourceBundle(locale, 'translation') &&
			!fetchingLocale[locale]
		) {
			fetchingLocale[locale] = true;
			const resources = await getLanguageResources(locale);
			if (resources) {
				Object.entries(resources).forEach(([namespace, bundle]) => {
					if (!i18n.hasResourceBundle(locale, namespace)) {
						i18n.addResourceBundle(locale, namespace, bundle, true, true);
						fetchingLocale[locale] = false;
						if (i18n.language === locale) {
							void i18n.changeLanguage(locale);
						}
					}
				});
			}
		}
	} catch {
		// No Error handling
	}
});

export const getDayjsLocaleKey = () => {
	switch (i18n.language as Language) {
		case Language.SIMPLIFIED_CHINESE:
			return 'zh-cn';
		case Language.TRADITIONAL_CHINESE:
			return 'zh-hk';
		case Language.ENGLISH:
		default:
			return 'en';
	}
};

export const isDefaultLanguage = () =>
	(i18n.language as Language) === DEFAULT_LANGUAGE;

export const applyQueryLanguageOverride = (): Language | null => {
	const lang = getQueryLanguageOverride();
	if (lang) {
		void i18n.changeLanguage(lang);
	}
	return lang;
};

export * from 'react-i18next';
export * from './const';
export * from './queryLanguageOverride';
export { i18n, i18p, init, t, z, ZodSchema };
